import { useState, useEffect, useCallback, useRef, useMemo } from 'react'

export default function StudentSelector({ graph = 'grammar', students = [], initialStudentId, onMasteryLoaded }) {
  const [query, setQuery] = useState('')
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [loading, setLoading] = useState(false)
  const blurTimeout = useRef(null)
  const inputRef = useRef(null)

  const filteredStudents = useMemo(() => {
    if (!query.trim()) return students.slice(0, 8)
    const q = query.toLowerCase()
    return students.filter(s => s.full_name.toLowerCase().includes(q)).slice(0, 8)
  }, [students, query])

  const fetchMastery = useCallback(async (studentId) => {
    if (!studentId) {
      onMasteryLoaded?.(null)
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`/teacher/concepts/${graph}/student_mastery?student_id=${studentId}`)
      if (response.ok) {
        const data = await response.json()
        onMasteryLoaded?.(data.masteries)
      } else {
        onMasteryLoaded?.(null)
      }
    } catch (err) {
      console.error('Failed to load mastery data:', err)
      onMasteryLoaded?.(null)
    } finally {
      setLoading(false)
    }
  }, [graph, onMasteryLoaded])

  // Auto-select student from URL param on mount
  useEffect(() => {
    if (initialStudentId && students.length > 0) {
      const student = students.find(s => s.id === initialStudentId)
      if (student) {
        setSelectedStudent(student)
        setQuery(student.full_name)
        fetchMastery(student.id)
      }
    }
  }, [initialStudentId, students]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (student) => {
    setSelectedStudent(student)
    setQuery(student.full_name)
    setShowDropdown(false)
    fetchMastery(student.id)
  }

  const handleClear = () => {
    setSelectedStudent(null)
    setQuery('')
    setShowDropdown(false)
    onMasteryLoaded?.(null)
    inputRef.current?.focus()
  }

  const handleInputChange = (e) => {
    setQuery(e.target.value)
    setSelectedStudent(null)
    setShowDropdown(true)
    if (!e.target.value) onMasteryLoaded?.(null)
  }

  const handleFocus = () => {
    clearTimeout(blurTimeout.current)
    if (!selectedStudent) setShowDropdown(true)
  }

  const handleBlur = () => {
    blurTimeout.current = setTimeout(() => setShowDropdown(false), 150)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowDropdown(false)
      inputRef.current?.blur()
    }
  }

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-3">
      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
        Student overlay
      </label>
      <div className="relative">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search by name…"
            value={query}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className="flex-1 text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
          />
          {loading && (
            <span className="text-xs text-gray-400 self-center">Loading…</span>
          )}
        </div>

        {showDropdown && filteredStudents.length > 0 && (
          <ul className="absolute z-20 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
            {filteredStudents.map(s => (
              <li key={s.id}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(s)}
                  className="w-full text-left text-xs px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {s.full_name}
                </button>
              </li>
            ))}
          </ul>
        )}

        {showDropdown && query.trim() && filteredStudents.length === 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg text-xs text-gray-400">
            No students found
          </div>
        )}
      </div>

      {selectedStudent && !loading && (
        <button
          onClick={handleClear}
          className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 mt-1"
        >
          Clear overlay
        </button>
      )}
    </div>
  )
}
